import * as THREE from "three";
import { cheapTextures } from "./mapTextures.js";

/**
 * Procedural textures for WORLD DECALS — the marks a fight leaves on the map:
 * bullet craters, explosion scorch, blood pools, the wet patch a hose paints on
 * a wall, and (2026-08-01) the two MOVEMENT marks: boot prints and tyre skids.
 * Canvas-generated (no asset files), module-cached, one GPU upload
 * each, built lazily and never rebuilt — the same contract as `mapTextures` /
 * `combatTextures` and their six siblings.
 *
 * MULTIPLY SEMANTICS. Every texture here is authored for `THREE.MultiplyBlending`
 * (dst *= src), so the value that means "nothing here" is WHITE, not alpha 0:
 *
 *   - a decal never needs an alpha channel or a sorted transparent pass, because
 *     multiplication COMMUTES — two overlapping decals compose to the same frame
 *     in either draw order. That is what lets the whole system be six
 *     InstancedMeshes with no per-decal sorting and no depth pre-pass.
 *   - fading a decal out is a lerp of its instance colour toward white, which is
 *     one `setColorAt` — no per-instance alpha attribute, so no custom shader and
 *     no extra program-cache variant beyond the one `renderer.ts` warms.
 *   - the mark inherits the surface underneath it. A scorch on pale concrete and
 *     the same scorch on dark asphalt both read correctly, because both are the
 *     surface's own colour times the same soot ramp. A blended sprite would have
 *     to guess the surface colour and gets it wrong on half the maps.
 *
 * So: every texel is `mix(white, markColour, mask)`, and every texture MUST fall
 * to pure white before its quad edge or the square shows.
 *
 * Budget (full / `cheapTextures()`): crater 128²/64², scorch 256²/128²,
 * blood 128²/64², wet 128²/64², boot 128²/64², skid 128²/64².
 * ~0.46 MB pre-mip full, ~0.12 MB cheap.
 */

/** Deterministic hash noise — same helper as combatTextures/fxTextures. A fixed
 *  pattern keeps an A/B screenshot reproducible. */
function rnd(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function cvs(w: number, h: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  return { c, g: c.getContext("2d")! };
}

/** Value noise on an integer lattice, smoothstep-interpolated. Not tiled — every
 *  decal is a single quad, never a repeat. */
function noiseField(S: number, cells: number, seed: number): Float32Array {
  const r = rnd(seed);
  const g = new Float32Array((cells + 1) * (cells + 1));
  for (let i = 0; i < g.length; i++) g[i] = r();
  const out = new Float32Array(S * S);
  const sm = (t: number): number => t * t * (3 - 2 * t);
  for (let y = 0; y < S; y++) {
    const fy = (y / S) * cells, gy = Math.floor(fy), ty = sm(fy - gy);
    for (let x = 0; x < S; x++) {
      const fx = (x / S) * cells, gx = Math.floor(fx), tx = sm(fx - gx);
      const a = g[gy * (cells + 1) + gx], b = g[gy * (cells + 1) + gx + 1];
      const c = g[(gy + 1) * (cells + 1) + gx], d = g[(gy + 1) * (cells + 1) + gx + 1];
      const top = a + (b - a) * tx;
      out[y * S + x] = top + ((c + (d - c) * tx) - top) * ty;
    }
  }
  return out;
}

/** Sum of octaves of `noiseField`, normalised to roughly 0..1. */
function fbm(S: number, base: number, oct: number, seed: number): Float32Array {
  const out = new Float32Array(S * S);
  let amp = 1, norm = 0;
  for (let o = 0; o < oct; o++) {
    const n = noiseField(S, base << o, seed + o * 7919);
    for (let i = 0; i < out.length; i++) out[i] += n[i] * amp;
    norm += amp;
    amp *= 0.5;
  }
  for (let i = 0; i < out.length; i++) out[i] /= norm;
  return out;
}

/** Finish a mask+colour buffer into a texture. `mask[i]` is 0 (no mark) .. 1
 *  (full mark); the mark colour is per-texel so blood can darken toward red
 *  while its dry edge goes brown. */
function toTexture(
  S: number, mask: Float32Array, cr: Float32Array, cg: Float32Array, cb: Float32Array,
  /** Decals that TILE ALONG A PATH (prints, skids) are laid down in their
   *  hundreds and are often seen at a grazing angle at range. There, mip
   *  minification averages the mark's dark texels over the WHOLE quad and every
   *  one of them turns into a uniform grey SQUARE on the ground — the quad's own
   *  silhouette, which is exactly what `rimKill` exists to prevent at mip 0.
   *  These marks are small (≤ 0.9 m) and low-contrast, so dropping the mip chain
   *  costs a little shimmer at distance and buys back the silhouette. The four
   *  event marks keep their mips: they are bigger, rarer, and read as smudges
   *  rather than as tiles when minified. */
  noMips = false,
): THREE.CanvasTexture {
  const { c, g } = cvs(S, S);
  const img = g.createImageData(S, S);
  const d = img.data;
  for (let i = 0; i < S * S; i++) {
    const m = mask[i] < 0 ? 0 : mask[i] > 1 ? 1 : mask[i];
    d[i * 4] = Math.round(255 * (1 - m * (1 - cr[i])));
    d[i * 4 + 1] = Math.round(255 * (1 - m * (1 - cg[i])));
    d[i * 4 + 2] = Math.round(255 * (1 - m * (1 - cb[i])));
    d[i * 4 + 3] = 255; // multiply blending ignores alpha; keep it opaque
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.anisotropy = noMips ? 8 : 4;
  if (noMips) {
    t.generateMipmaps = false;
    t.minFilter = THREE.LinearFilter;
  }
  return t;
}

/** Cosine-ish edge kill: forces the outer ring of every decal to pure white so
 *  the quad's own square silhouette can never show. */
function rimKill(rad: number): number {
  if (rad >= 0.98) return 0;
  if (rad <= 0.86) return 1;
  const t = (0.98 - rad) / 0.12;
  return t * t;
}

let craterTex: THREE.CanvasTexture | null = null;

/**
 * A BULLET CRATER: a near-black punched core, a soot halo warped by fBm so the
 * mark is not a clean circle, radial cracks spidering out of it, and a fine
 * dust speckle. The cracks are what make the mark read as damage rather than as
 * a sticker — a plain dark dot at 6 cm is indistinguishable from dirt.
 */
export function bulletCraterTexture(): THREE.CanvasTexture {
  if (craterTex) return craterTex;
  const S = cheapTextures() ? 64 : 128;
  const warp = fbm(S, 4, 3, 0x5eed1);
  const grain = fbm(S, 12, 2, 0x0c4a1);
  const mask = new Float32Array(S * S);
  const cr = new Float32Array(S * S), cg = new Float32Array(S * S), cb = new Float32Array(S * S);
  const r = rnd(0x1bad);
  // radial cracks: angle, length, taper
  const CRACKS = 7;
  const ca = new Float32Array(CRACKS), cl = new Float32Array(CRACKS);
  for (let i = 0; i < CRACKS; i++) {
    ca[i] = (i / CRACKS) * Math.PI * 2 + r() * 0.6;
    cl[i] = 0.34 + r() * 0.42;
  }
  const cx = (S - 1) / 2, cy = (S - 1) / 2;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const dx = (x - cx) / (S / 2), dy = (y - cy) / (S / 2);
      const rad = Math.hypot(dx, dy);
      const ang = Math.atan2(dy, dx);
      // lobed soot halo — the fBm pushes the halo edge in/out by ±25%
      const edge = 0.5 * (0.78 + (warp[i] - 0.5) * 0.5);
      let m = Math.max(0, 1 - rad / Math.max(0.05, edge));
      m = m * m * 0.72;
      // punched core
      m += Math.max(0, 1 - rad / 0.17) * 1.4;
      // cracks
      for (let k = 0; k < CRACKS; k++) {
        let da = ang - ca[k];
        while (da > Math.PI) da -= Math.PI * 2;
        while (da < -Math.PI) da += Math.PI * 2;
        if (rad > cl[k]) continue;
        const width = 0.035 + rad * 0.05; // fans slightly with distance
        const across = Math.abs(da) * rad;
        if (across < width) m += (1 - across / width) * (1 - rad / cl[k]) * 0.85;
      }
      m += (grain[i] - 0.5) * 0.14 * Math.max(0, 1 - rad / 0.7); // dust speckle
      m *= rimKill(rad);
      mask[i] = m;
      // the crater floor is near-black and slightly warm (exposed substrate);
      // the halo is neutral soot
      const core = Math.max(0, 1 - rad / 0.2);
      cr[i] = 0.13 + core * 0.03;
      cg[i] = 0.12;
      cb[i] = 0.12 + (1 - core) * 0.02;
    }
  }
  craterTex = toTexture(S, mask, cr, cg, cb);
  return craterTex;
}

let scorchTex: THREE.CanvasTexture | null = null;

/**
 * EXPLOSION SCORCH: a wide soot smear. Built as a heavily fBm-warped radial with
 * ~18 outward streaks that carry soot past the core, plus mottling so it never
 * looks airbrushed. The centre stops just short of black — real scorch on
 * concrete is dark grey with a warm ring of heat-stained substrate around it,
 * and that ring is what tells you a fire happened rather than a paint spill.
 */
export function scorchTexture(): THREE.CanvasTexture {
  if (scorchTex) return scorchTex;
  const S = cheapTextures() ? 128 : 256;
  const warp = fbm(S, 3, 4, 0x9a17c);
  const mottle = fbm(S, 9, 3, 0x33f0e);
  const mask = new Float32Array(S * S);
  const cr = new Float32Array(S * S), cg = new Float32Array(S * S), cb = new Float32Array(S * S);
  const r = rnd(0xc0a1);
  const N = 18;
  const sa = new Float32Array(N), sl = new Float32Array(N), sw = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    sa[i] = (i / N) * Math.PI * 2 + r() * 0.5;
    sl[i] = 0.55 + r() * 0.42;
    sw[i] = 0.07 + r() * 0.12;
  }
  const cx = (S - 1) / 2, cy = (S - 1) / 2;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const dx = (x - cx) / (S / 2), dy = (y - cy) / (S / 2);
      const rad = Math.hypot(dx, dy);
      const ang = Math.atan2(dy, dx);
      const edge = 0.62 * (0.8 + (warp[i] - 0.5) * 0.72);
      let m = Math.max(0, 1 - rad / Math.max(0.06, edge));
      m = Math.pow(m, 1.35);
      for (let k = 0; k < N; k++) {
        let da = ang - sa[k];
        while (da > Math.PI) da -= Math.PI * 2;
        while (da < -Math.PI) da += Math.PI * 2;
        if (rad > sl[k]) continue;
        if (Math.abs(da) < sw[k]) {
          m += (1 - Math.abs(da) / sw[k]) * (1 - rad / sl[k]) * 0.5;
        }
      }
      m *= 0.6 + mottle[i] * 0.7;
      m *= rimKill(rad);
      mask[i] = Math.min(1.15, m);
      // soot core → heat-stained warm ring at the edge of the burn
      const ring = Math.max(0, 1 - Math.abs(rad - 0.5) / 0.24);
      cr[i] = 0.14 + ring * 0.24;
      cg[i] = 0.13 + ring * 0.11;
      cb[i] = 0.14;
    }
  }
  scorchTex = toTexture(S, mask, cr, cg, cb);
  return scorchTex;
}

let bloodTex: THREE.CanvasTexture | null = null;

/**
 * A BLOOD POOL: one irregular central pool, a ring of cast-off droplets and a
 * few elongated flick streaks. Multiply is exactly right for blood — the mark is
 * a dark red FILTER over whatever it lands on, which is why the same texture
 * works on sand, steel decking and neon-lit concrete.
 */
export function bloodTexture(): THREE.CanvasTexture {
  if (bloodTex) return bloodTex;
  const S = cheapTextures() ? 64 : 128;
  const warp = fbm(S, 4, 3, 0x0b100d);
  const mask = new Float32Array(S * S);
  const cr = new Float32Array(S * S), cg = new Float32Array(S * S), cb = new Float32Array(S * S);
  const r = rnd(0xb100);
  // satellite droplets: polar position, radius, elongation along the radius
  // Deliberately UNEVEN: an even ring of equal droplets reads as a flower, not
  // as spatter. Radii are biased toward the pool (r²) so most droplets cling to
  // the edge, sizes span 4x, and only the far ones elongate.
  const N = 26;
  const da = new Float32Array(N), dr = new Float32Array(N), ds = new Float32Array(N), de = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const u = r();
    da[i] = r() * Math.PI * 2;
    dr[i] = 0.3 + u * u * 0.62;
    ds[i] = 0.016 + r() * r() * 0.075;
    de[i] = 1 + u * u * 2.2;
  }
  const cx = (S - 1) / 2, cy = (S - 1) / 2;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const dx = (x - cx) / (S / 2), dy = (y - cy) / (S / 2);
      const rad = Math.hypot(dx, dy);
      const edge = 0.4 * (0.74 + (warp[i] - 0.5) * 0.85);
      let m = rad < edge ? 1 : 0;
      // soft rim so the pool has a wet meniscus rather than a cut edge
      if (rad >= edge && rad < edge + 0.06) m = 1 - (rad - edge) / 0.06;
      for (let k = 0; k < N; k++) {
        const px = Math.cos(da[k]) * dr[k], py = Math.sin(da[k]) * dr[k];
        // stretch the droplet along its own radius (cast-off direction)
        const ux = Math.cos(da[k]), uy = Math.sin(da[k]);
        const ax = dx - px, ay = dy - py;
        const along = ax * ux + ay * uy, across = ax * -uy + ay * ux;
        const dd = Math.hypot(along / de[k], across);
        if (dd < ds[k]) m = Math.max(m, 1 - (dd / ds[k]) * 0.55);
      }
      m *= rimKill(rad);
      mask[i] = m;
      // deep arterial red in the pool, browner at the thin edge (drying)
      const thin = Math.max(0, Math.min(1, (rad - edge * 0.55) / Math.max(0.05, edge * 0.5)));
      cr[i] = 0.30 + thin * 0.16;
      cg[i] = 0.030 + thin * 0.06;
      cb[i] = 0.035 + thin * 0.03;
    }
  }
  bloodTex = toTexture(S, mask, cr, cg, cb);
  return bloodTex;
}

let wetTex: THREE.CanvasTexture | null = null;

/**
 * A WET PATCH — what the sperm gun and the water gun leave where their stream
 * lands. A wet surface is a DARKER, slightly cooler version of the dry one (the
 * film kills diffuse scatter), which is exactly a multiply, so this needs no
 * per-material wetness and works on every surface in the game.
 *
 * Shape: a splayed splat with a bright-ish centre (thin film), a darker rim
 * where the liquid pools, satellite spray, and four DRIP tails running down -V.
 * The quad is placed with +V up the surface, so the drips run downhill.
 */
export function wetSplatTexture(): THREE.CanvasTexture {
  if (wetTex) return wetTex;
  const S = cheapTextures() ? 64 : 128;
  const warp = fbm(S, 4, 3, 0x2fa11);
  const fine = fbm(S, 11, 2, 0x71c3);
  const mask = new Float32Array(S * S);
  const cr = new Float32Array(S * S), cg = new Float32Array(S * S), cb = new Float32Array(S * S);
  const r = rnd(0x5150);
  const N = 16;
  const da = new Float32Array(N), dr = new Float32Array(N), ds = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    da[i] = r() * Math.PI * 2;
    dr[i] = 0.36 + r() * 0.48;
    ds[i] = 0.02 + r() * 0.055;
  }
  const DRIPS = 4;
  const dxk = new Float32Array(DRIPS), dlk = new Float32Array(DRIPS), dwk = new Float32Array(DRIPS);
  for (let i = 0; i < DRIPS; i++) {
    dxk[i] = (r() - 0.5) * 0.6;
    dlk[i] = 0.35 + r() * 0.5;
    dwk[i] = 0.018 + r() * 0.026;
  }
  const cx = (S - 1) / 2, cy = (S - 1) / 2;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const dx = (x - cx) / (S / 2), dy = (y - cy) / (S / 2);
      // canvas +y is DOWN the texture, and the quad is placed +V up, so "below
      // the splat" in world space is +dy here
      const rad = Math.hypot(dx, dy);
      const edge = 0.36 * (0.78 + (warp[i] - 0.5) * 0.8);
      let m = Math.max(0, 1 - rad / Math.max(0.05, edge));
      m = Math.pow(m, 0.55); // flat film, sharp-ish edge
      // pooled rim: wettest right at the boundary
      m += Math.max(0, 1 - Math.abs(rad - edge) / 0.06) * 0.35;
      for (let k = 0; k < N; k++) {
        const px = Math.cos(da[k]) * dr[k], py = Math.sin(da[k]) * dr[k];
        const dd = Math.hypot(dx - px, dy - py);
        if (dd < ds[k]) m = Math.max(m, (1 - dd / ds[k]) * 0.85);
      }
      for (let k = 0; k < DRIPS; k++) {
        if (dy < 0) continue; // only below the splat
        const t = dy / dlk[k];
        if (t > 1) continue;
        const w = dwk[k] * (1 - t * 0.4);
        const off = Math.abs(dx - dxk[k] - Math.sin(t * 4 + k) * 0.02);
        if (off < w) m = Math.max(m, (1 - off / w) * (1 - t) * 0.9);
      }
      m *= 0.82 + fine[i] * 0.3;
      m *= rimKill(rad);
      mask[i] = Math.min(1, m);
      // wet = darker + slightly cooler than dry
      cr[i] = 0.50; cg[i] = 0.54; cb[i] = 0.62;
    }
  }
  wetTex = toTexture(S, mask, cr, cg, cb);
  return wetTex;
}

let bootTex: THREE.CanvasTexture | null = null;

/**
 * A BOOT PRINT — the outsole an avatar leaves behind on dust, mud or wet floor.
 *
 * Unlike the four marks above, this one is not damage: it is the record of a
 * PATH. That changes what it has to be good at. A single print is read at a
 * glance from three metres up while you run past it, so the silhouette has to
 * survive at ~20 px: two separated pads (forefoot + heel) with a pinched arch
 * between them is the shape the eye recognises as a footprint, and it is the
 * only part of a real sole that reads at that size. Everything else here —
 * lugs, rim, grain — exists to stop the pads looking like two grey pills.
 *
 * The texture is authored TOE-UP: canvas y = 0 is the top of the image, which
 * `CanvasTexture`'s default flipY maps to v = 1, which the decal quad's local +Y
 * is. `decals.step()` rolls the quad so local +Y points along the walker's
 * facing, so the toe points the way they were going.
 *
 * Deliberately WEAK (mask caps around 0.55, colour a dusty neutral). A print is
 * a scuff, not a stain; at full strength a jogging bot paints a black dashed
 * line across the map.
 */
export function bootPrintTexture(): THREE.CanvasTexture {
  if (bootTex) return bootTex;
  const S = cheapTextures() ? 64 : 128;
  const grain = fbm(S, 10, 3, 0xb007);
  const warp = fbm(S, 4, 2, 0x501e);
  const mask = new Float32Array(S * S);
  const cr = new Float32Array(S * S), cg = new Float32Array(S * S), cb = new Float32Array(S * S);
  const cx = (S - 1) / 2, cy = (S - 1) / 2;
  // pads, in normalised quad space (dy < 0 = toward the toe)
  const FX = 0, FY = -0.34, FW = 0.28, FH = 0.44; // forefoot
  const HX = 0, HY = 0.50, HW = 0.235, HH = 0.28; // heel
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const dx = (x - cx) / (S / 2), dy = (y - cy) / (S / 2);
      // the sole edge wobbles a little so 200 prints don't look stamped
      const wob = (warp[i] - 0.5) * 0.09;
      const fd = Math.hypot((dx - FX) / (FW + wob), (dy - FY) / FH);
      const hd = Math.hypot((dx - HX) / (HW + wob), (dy - HY) / HH);
      // Flat-topped, but it MUST reach zero at the pad edge: a profile with a
      // floor (the first version fell only to 0.65) makes the two pads merge
      // into one long blob and the arch — the single feature that makes the
      // silhouette read as a footprint — disappears.
      let m = 0;
      if (fd < 1) m = Math.max(m, Math.pow(1 - fd * fd, 0.42));
      if (hd < 1) m = Math.max(m, Math.pow(1 - hd * hd, 0.42));
      if (m > 0) {
        // LUGS: transverse tread bars, chevroned (the bar bends toward the toe
        // at the sole's centre line) — the detail that says "boot" and not
        // "blob" once you are standing over it.
        const bar = dy * 9.2 + Math.abs(dx) * 2.6;
        const lug = 0.62 + 0.38 * (0.5 + 0.5 * Math.cos(bar * Math.PI));
        m *= lug;
        // welt: the sole's outer rim presses hardest
        const rim = Math.max(fd < 1 ? fd : 0, hd < 1 ? hd : 0);
        m *= 0.86 + rim * rim * 0.3;
        m *= 0.78 + grain[i] * 0.4;
        m *= 0.72;
      }
      // elliptical edge kill — the print is tall and narrow, so a circular
      // rimKill would either clip the toe or leave the sides live
      m *= rimKill(Math.hypot(dx / 0.42, dy / 0.9));
      mask[i] = m < 0 ? 0 : m;
      // dry dust/grime: neutral, a hair warm. Multiply means this darkens sand,
      // decking and neon concrete each by the right amount without knowing which.
      cr[i] = 0.44; cg[i] = 0.42; cb[i] = 0.38;
    }
  }
  bootTex = toTexture(S, mask, cr, cg, cb, true);
  return bootTex;
}

let skidTex: THREE.CanvasTexture | null = null;

/**
 * A TYRE SKID — laid down by a kart/truck whose wheels are sliding sideways.
 *
 * Stamped repeatedly along the wheel's path (`decals.skid()`), so the ONE thing
 * this texture must get right is that consecutive stamps join into a continuous
 * black line: the mark has to fall to white at BOTH ends along +V/-V, smoothly,
 * with no hard cap, or the trail reads as a dotted line of tiles. The
 * longitudinal grooves are unbroken end-to-end for the same reason — a groove
 * that faded independently would strobe as the stamps overlapped.
 */
export function skidTexture(): THREE.CanvasTexture {
  if (skidTex) return skidTex;
  const S = cheapTextures() ? 64 : 128;
  const mottle = fbm(S, 7, 3, 0x5c1d);
  const fine = fbm(S, 16, 2, 0x7a1e);
  const mask = new Float32Array(S * S);
  const cr = new Float32Array(S * S), cg = new Float32Array(S * S), cb = new Float32Array(S * S);
  const cx = (S - 1) / 2, cy = (S - 1) / 2;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const dx = (x - cx) / (S / 2), dy = (y - cy) / (S / 2);
      // the contact patch wanders a few cm as the tyre walks under load
      const wander = Math.sin(dy * 3.1) * 0.045;
      const across = Math.abs(dx - wander) / 0.46;
      if (across >= 1) { mask[i] = 0; cr[i] = cg[i] = cb[i] = 0.2; continue; }
      // flat-topped across the tread, soft at the shoulders
      let m = Math.pow(1 - across * across, 0.45);
      // LONGITUDINAL GROOVES: unbroken along the whole stamp so overlapping
      // stamps line their grooves up instead of beating against each other
      const grooves = 0.72 + 0.28 * Math.abs(Math.sin((dx - wander) * 11.5));
      m *= grooves;
      // rubber lays down unevenly — hot patches and skips
      m *= 0.58 + mottle[i] * 0.72;
      m *= 0.85 + fine[i] * 0.3;
      // smooth ends: squared taper over the outer 34% of each end
      const t = Math.min(1, Math.max(0, (Math.abs(dy) - 0.62) / 0.34));
      m *= (1 - t) * (1 - t);
      // ...and an elliptical rim kill on top, because the taper alone still
      // leaves a few percent of mark alive at the corners of the tread band,
      // which is enough to draw the quad's own square edge on the road
      m *= rimKill(Math.hypot(dx / 0.52, dy / 0.96));
      mask[i] = Math.min(1, m);
      // Not as black as real rubber looks in isolation: consecutive stamps
      // OVERLAP, and multiply compounds (two 0.21 stamps = 0.044, i.e. solid
      // black). Photographed 2026-08-01 — the first trail read as a chain of ink
      // blots. 0.34 with the callers' ~0.7 m spacing lands the doubled-up
      // sections around 0.2, which is what a skid actually looks like.
      cr[i] = 0.34; cg[i] = 0.335; cb[i] = 0.35; // scrubbed rubber
    }
  }
  skidTex = toTexture(S, mask, cr, cg, cb, true);
  return skidTex;
}

let contactTex: THREE.CanvasTexture | null = null;

/**
 * THE CONTACT SHADOW — the soft dark patch a body/prop/machine casts straight
 * down onto whatever it is standing on (contactShadows.ts, 2026-08-05).
 *
 * It lives in this file because it is the same kind of object as every mark
 * above: a MULTIPLY texture that inherits the surface under it. That is the
 * whole reason one 256² sheet can ground an avatar on grass, on wet decking and
 * on a neon roof without being told which — a shadow is not a grey sprite, it is
 * "this surface, times less light".
 *
 * Three things this must get right, all learned from the marks above:
 *
 *  - **A soft PENUMBRA, not a disc.** Real contact occlusion is near-black where
 *    the body touches and washes out fast; the profile here is a squared
 *    falloff with an umbra plateau over the inner ~28%, so a running avatar
 *    reads as connected to the ground rather than as sitting on a grey coin.
 *  - **An irregular EDGE.** A perfect circle is the single loudest tell that a
 *    shadow is fake. The radius is warped by two low-frequency fBm octaves
 *    (±9%), and the falloff exponent itself wobbles, so the boundary is neither
 *    round nor uniformly soft.
 *  - **A COOL tint.** The light a contact shadow removes is the sky's, so what
 *    is left is warm-deficient. A neutral grey multiplier reads as dirt; a
 *    slightly blue one reads as shade. The tint is strongest in the umbra and
 *    relaxes toward the penumbra, which is what real skylight does.
 *
 * Mips are KEPT (unlike prints/skids): a blob is big, smooth and low-contrast,
 * so minification blurs it into exactly what it already is instead of averaging
 * a hard mark into a grey square.
 */
export function contactShadowTexture(): THREE.CanvasTexture {
  if (contactTex) return contactTex;
  const S = cheapTextures() ? 128 : 256;
  const warp = fbm(S, 3, 2, 0x5aad0);
  const grain = fbm(S, 9, 2, 0x0cc1);
  const mask = new Float32Array(S * S);
  const cr = new Float32Array(S * S), cg = new Float32Array(S * S), cb = new Float32Array(S * S);
  const c0 = (S - 1) / 2;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const dx = (x - c0) / (S / 2), dy = (y - c0) / (S / 2);
      // the occluder is a body, not a sphere: its outline breathes
      const r = Math.hypot(dx, dy) / (0.92 + (warp[i] - 0.5) * 0.18);
      let m = 0;
      if (r < 1) {
        // umbra plateau, then a squared penumbra with a wobbling exponent
        // The plateau is WIDE (inner 45%) on purpose. Photographed at ×6 with a
        // narrow one: the boots cover the umbra and all you see of the shadow is
        // penumbra, which reads as a smudge rather than as contact.
        const t = Math.max(0, (r - 0.45) / 0.55);
        const p = 1.25 + (warp[i] - 0.5) * 0.6;
        m = Math.pow(1 - t * t, p);
        // ground texture shows THROUGH a soft shadow — a perfectly smooth ramp
        // is the second tell after a perfect circle
        m *= 0.9 + grain[i] * 0.2;
      }
      m *= rimKill(Math.hypot(dx, dy));
      m = m < 0 ? 0 : m > 1 ? 1 : m;
      // Peak darkening. Photographed 2026-08-05 against a real GPU: the first
      // strength (0.62 mask over a 0.33 tint) rendered as a ~15% darkening and
      // was INVISIBLE under a body on grey paving — the same "the ink was the
      // problem, not the system" trap the floor-contact pass hit a day earlier.
      // A contact shadow directly under a standing body is one of the darkest
      // things in a daylight frame; this lands the umbra near 0.26.
      mask[i] = m; // the field scales it per caster (strength → instanceColor)
      // What a contact shadow removes is SKYLIGHT, which is blue — so the
      // multiplier must take more red than blue away, leaving the shaded
      // patch cooler than the lit surface beside it. (Under multiply, a
      // HIGHER channel value = less of that channel removed.)
      cr[i] = 0.13;
      cg[i] = 0.145;
      cb[i] = 0.19;
    }
  }
  contactTex = toTexture(S, mask, cr, cg, cb);
  return contactTex;
}

/** Build all six under the load screen. Decals are, by definition, first used
 *  mid-fight — the same reason `prewarmCombatTextures` exists. */
export function prewarmDecalTextures(): void {
  contactShadowTexture();
  bulletCraterTexture();
  scorchTexture();
  bloodTexture();
  wetSplatTexture();
  bootPrintTexture();
  skidTexture();
}
